import { bounceCount, recordBounce } from './api'

/**
 * Hand an unauthenticated reader to the real sign-in page (TASK-025).
 *
 * A session good enough for /v1/auth/me but not for this resource would
 * otherwise ping-pong between the two pages forever. Two attempts, then stop
 * and say so, which is at least a dead end a person can read.
 *
 * Returns false when it gave up, and the caller renders that dead end.
 *
 * location.replace(), not a client-side route change: the sign-in page has to
 * be reached with a real navigation so a stale session cookie is re-presented
 * and so the back button does not return to a page that already refused.
 */
export function redirectToLogin(): boolean {
  const bounces = bounceCount()
  if (bounces >= 2) return false
  recordBounce(bounces + 1)

  /*
   * Carry ?auth_error across the bounce.
   *
   * /auth/link and /auth/callback/google redirect a failed browser sign-in to
   * `/?auth_error=<code>`. Whoever lands there is, by definition, not signed
   * in -- so the document list 401s and sends them straight here, and the one
   * sentence explaining why their emailed link did not work would be thrown
   * away with the rest of the query string. Lifting it out of `next` and onto
   * the sign-in page's own URL puts the explanation in front of the person at
   * the moment they are being asked to try again.
   */
  const params = new URLSearchParams(location.search)
  const authError = params.get('auth_error')
  params.delete('auth_error')
  const rest = params.toString()

  const query = new URLSearchParams({ next: location.pathname + (rest ? `?${rest}` : '') })
  if (authError) query.set('auth_error', authError)

  location.replace(`/login?${query.toString()}`)
  return true
}
